import type {Signal} from '@pexip/signal';
import type {Backoff} from '@pexip/utils';
import type {
    ConferenceStatusMap,
    ParticipantsMap,
    TransformLayoutMap,
    FeccParticipantMap,
    DialMap,
    InfinityErrorMessage,
    InfinityErrorCode,
    TransferParticipantMap,
    unlockParticipant,
    disconnect,
    disconnectParticipant,
    dial,
    muteParticipant,
    unmuteParticipant,
    muteguests,
    unmuteguests,
    videoMuteParticipant,
    videoUnmuteParticipant,
    lock,
    unlock,
    transformLayout,
    buzzParticipant,
    clearbuzzParticipant,
    spotlightonParticipant,
    spotlightoffParticipant,
    ack,
    update,
    newCandidate,
    callsWebrtcParticipant,
    takeFloor,
    releaseFloor,
    transferParticipant,
    CallsWebrtcParticipantMap,
    StatisticsMap,
    ThemeMap,
    MessageConferenceMap,
    messageConference,
    messageParticipant,
    requestToken,
    RequestTokenMap,
    overlaytextParticipant,
    OverlaytextParticipantMap,
    dtmfParticipant,
    dtmf,
    preferredAspectRatio,
    BreakoutsMap,
    showLiveCaptions,
    hideLiveCaptions,
    participants,
    startConference,
    breakoutsDisconnect,
    breakoutsEmpty,
    feccParticipant,
    FeccMap,
    fecc,
    presInMixParticipant,
    guestLeaveBreakout,
    guestBreakoutBuzz,
    guestClearBreakoutBuzz,
    hostClearBreakoutBuzz,
    hostBreakoutBuzz,
    notifyNotAFK,
    clearAllBuzz,
    clientMuteParticipant,
    clientUnmuteParticipant,
    setGuestsCanUnmute,
    availableLayouts,
    videoMixesConfigure,
    videoMixesDelete,
    breakoutMoveParticipants,
    breakoutMoveParticipantsFromMain,
    breakoutRequestGuestToken,
    RegistrationsMap,
    liveCaptionsLanguages,
} from '@pexip/infinity-api';
import type {
    AudioQualityStats,
    CallQualityStats,
    InboundAudioMetrics,
    InboundVideoMetrics,
    NormalizedRTCStats,
    OutboundAudioMetrics,
    OutboundVideoMetrics,
    Quality,
    StatsCollector,
    StatsSignals,
} from '@pexip/peer-connection-stats';
import type {
    Bandwidth,
    CorePeerConnectionSignals,
    MainPeerConnectionOptions,
    PCOptionalsSignals,
    PeerConnectionCommandSignals,
    ErrorMessage as PCErrorMessage,
} from '@pexip/peer-connection';

import type {ClientCallType} from './clientCallType';
import type {LogMethod} from './baseLogger';

export type APIType = typeof import('@pexip/infinity-api');
export type MessageBody = MessageConferenceMap['Body'];
export type ConferenceStateEvent = ConferenceStatusMap['200']['result'];
// `last_spoken_time` field will not be updated via participant events
export type RTCParticipantEvent = Omit<
    ParticipantsMap['200']['result'][0],
    'last_spoken_time'
>;
export type Transforms = TransformLayoutMap['Body']['transforms'];
export type Layout = Transforms['layout'];
export type FeccEvent = Omit<FeccParticipantMap['Body'], 'target'>;
export type Role = TransferParticipantMap['Body']['role'];
export type Stun = RequestTokenMap['200']['result']['stun'];
export type Turn = RequestTokenMap['200']['result']['turn'];
export type ThemeSchema = ThemeMap['200']['result'];
export type Screen = ThemeSchema['direct_media_escalate'];
export type MediaType = CallsWebrtcParticipantMap['Body']['media_type'];
export type CurrentServiceType =
    | RTCParticipantEvent['service_type']
    | RequestTokenMap['200']['result']['current_service_type'];
export type DirectoryResult = RegistrationsMap['200']['result'][number];
interface RedirectIdp {
    name: string;
    uuid: string;
}
export type Guid<DataType> = string & {__guid: DataType};
export type RoomID = Guid<'RoomID'> | 'main';
export type ParticipantID = Guid<'Participant'>;

interface Idp extends RedirectIdp {
    img?: string;
    disablePopUpFlow?: boolean;
}

export interface RequestClient {
    token: string;
    expires: number;
    statsUpdateInterval?: number;

    fetcher: typeof fetch;
    refreshToken: () => Promise<boolean>;
    cleanup: (reason?: DisconnectReason) => Promise<void>;
}

export interface RefreshTokenResult {
    status: 200;
    data: {
        status: 'success';
        result: {
            token: string;
            role?: 'HOST' | 'GUEST';
            expires: string;
            client_stats_update_interval?: number;
        };
    };
}

export interface RequestClientOptions {
    backoff?: Backoff;
    expires?: number;
    fetcher?: typeof fetch;
    token: string;
    clientStatsUpdateInterval?: number;
    tokenExpiredCb?: () => void;
    tokenRefreshedCb?: (token: string) => void;
    refreshTokenFn: (
        fetcher: typeof fetch,
    ) => Promise<RefreshTokenResult | undefined>;
    releaseTokenFn: (
        fetcher: typeof fetch,
        body: {reason?: string},
    ) => Promise<void>;
    releaseTokenBeaconUrl: (token: string) => string;
}

export interface BreakoutRoom {
    breakout_uuid: RoomID;
    participant_uuid: ParticipantID;
}

export interface Client {
    roomId: RoomID;
    rooms: RoomID[];
    breakoutRooms: Map<RoomID, BreakoutRoom>;
    breakoutRoomNames: Map<RoomID, string>;
    conferenceStatus: Map<RoomID, ConferenceStatus>;
    conferenceFeatureFlags?: ConferenceFeatureFlags;
    secureCheckCode: string;
    latestStats: Stats | undefined;
    serviceType: CurrentServiceType;
    isFeccEnabled: boolean;
    conferenceName: string | undefined;
    /**
     * The callType that is being used for the current call
     */
    localCallType: ClientCallType;

    getMe(roomId?: RoomID): Participant | undefined;
    getParticipants(roomId: RoomID): Participant[];
    setFeccSupported(canFecc: boolean): void;

    isBreakoutRoom(roomId: RoomID): boolean;

    admit: (opt: {
        participantUuid: ParticipantID;
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof unlockParticipant>>>;

    call: (opt: {
        conferenceAlias: string;
        directMedia?: boolean;
        displayName: string | false;
        bandwidth: number;
        mediaStream?: MediaStream | (() => MediaStream | undefined);
        node?: string;
        host?: string;
        pin?: string;
        chosenIdp?: string;
        ssoToken?: string;
        registrationToken?: string;
        token?: string;
        conferenceExtension?: string;
        callTag?: string;
        allowChangeOfMediaDirection?: AllowMediaChangeOfDirection;
        isMeetingManager?: boolean;
        callType: ClientCallType;
        /**
         * DSCP markings for each media line
         * This has the same effect as priority, except that it only affects
         * the DSCP markings of the generated packets, as described in
         * [rfc8835] section 4.2.
         */
        networkPriorities?: MediaPriority;
        /**
         * Encoding priority for each media line
         * Indicates the priority of an RTCRtpSender, which influences the
         * bandwidth allocation among RTCRtpSender objects. It is specified in
         * [rfc8835], Section 4. The user agent is free to sub-allocate
         * bandwidth between the encodings of an RTCRtpSender.
         */
        encodingPriorities?: MediaPriority;
        degradationPreference?: DegradationPreference;
        jitterBufferTarget?: ReceiverJitterBufferTarget;
        clientId?: string;
        supportsDirectChat?: boolean;
        allowH264?: boolean;
        allowVP9?: boolean;
        maxFrameSize?: number;
    }) => Promise<undefined | Awaited<ReturnType<typeof requestToken>>>;

    disconnect: (opt: {
        callUuid?: string;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
        participantUuid?: ParticipantID;
        reason?: DisconnectReason;
    }) => Promise<void>;

    kick: (opt: {
        participantUuid: ParticipantID;
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof disconnectParticipant>>
    >;

    dial: (
        opt: Omit<DialMap['Body'], 'protocol'> & {
            destination: string;
            conferenceAlias?: string;
            host?: string;
            protocol?: DialMap['Body']['protocol'];
        },
    ) => Promise<undefined | Awaited<ReturnType<typeof dial>>>;

    transfer: (opt: {
        destination: string;
        pin: string;
        role: Role;
        participantUuid: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof transferParticipant>>>;

    mute: (opt: {
        mute: boolean;
        participantUuid?: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<ReturnType<typeof muteParticipant | typeof unmuteParticipant>>
    >;

    clientMute: (opt: {
        mute: boolean;
        participantUuid?: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<
                  typeof clientMuteParticipant | typeof clientUnmuteParticipant
              >
          >
    >;

    guestsCanUnmute: (opt: {
        setting: boolean;
        participantUuid?: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof setGuestsCanUnmute>>>;

    muteAllGuests: (opt: {
        mute: boolean;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof muteguests | typeof unmuteguests>>
    >;

    muteVideo: (opt: {
        muteVideo: boolean;
        participantUuid?: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<
                  typeof videoMuteParticipant | typeof videoUnmuteParticipant
              >
          >
    >;

    lock: (opt: {
        lock: boolean;
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof lock | typeof unlock>>>;

    disconnectAll: (opt: {
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof disconnect>>>;

    setLayout: (opt: {
        transforms: Transforms;
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof transformLayout>>>;

    setPersonalLayout: (opt: {
        transforms: Transforms;
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
        mixName?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof videoMixesConfigure>>>;

    deleteVideoMix: (opt: {
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
        mixName?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof videoMixesDelete>>>;

    sendMessage: (
        opt: MessageBody & {
            conferenceAlias?: string;
            host?: string;
            participantUuid?: ParticipantID;
            breakoutUuid?: RoomID;
        },
    ) => Promise<
        | undefined
        | Awaited<
              ReturnType<typeof messageConference | typeof messageParticipant>
          >
    >;

    raiseHand: (opt: {
        raise: boolean;
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        breakoutUuid?: RoomID;
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<typeof buzzParticipant | typeof clearbuzzParticipant>
          >
    >;

    lowerAllRaisedHands: (opt: {
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof clearAllBuzz>>>;

    spotlight: (opt: {
        enable: boolean;
        participantUuid: ParticipantID;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<
                  typeof spotlightonParticipant | typeof spotlightoffParticipant
              >
          >
    >;

    ack: (opt: {
        callUuid?: string;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
        participantUuid?: ParticipantID;
        sdp?: string;
        offerIgnored?: boolean;
    }) => Promise<undefined | Awaited<ReturnType<typeof ack>>>;

    update: (opt: {
        sdp: string;
        callUuid?: string;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
        participantUuid?: ParticipantID;
        abortSignal?: AbortSignal;
    }) => Promise<undefined | Awaited<ReturnType<typeof update>>>;

    newCandidate: (opt: {
        candidate: IceCandidate;
        callUuid?: string;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
        participantUuid?: ParticipantID;
    }) => Promise<undefined | Awaited<ReturnType<typeof newCandidate>>>;

    sendOffer: (opt: {
        sdp: string;
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        breakoutUuid?: RoomID;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof callsWebrtcParticipant>>
    >;

    takeFloor: (opts: {
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof takeFloor>>>;

    releaseFloor: (opts: {
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        breakoutUuid?: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof releaseFloor>>>;

    breakoutRequestGuestToken: (opts: {
        conferenceAlias?: string;
        host?: string;
        breakoutUuid?: RoomID;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof breakoutRequestGuestToken>>
    >;

    present: (stream?: MediaStream) => void;
    restartCall: (opts: {
        bandwidth: number;
        allowVP9?: boolean;
        maxFrameSize?: number;
        mediaStream?: MediaStream | (() => MediaStream | undefined);
        /**
         * DSCP markings for each media line
         * This has the same effect as priority, except that it only affects
         * the DSCP markings of the generated packets, as described in
         * [rfc8835] section 4.2.
         */
        networkPriorities?: MediaPriority;
        /**
         * Encoding priority for each media line
         * Indicates the priority of an RTCRtpSender, which influences the
         * bandwidth allocation among RTCRtpSender objects. It is specified in
         * [rfc8835], Section 4. The user agent is free to sub-allocate
         * bandwidth between the encodings of a RTCRtpSender.
         */
        encodingPriorities?: MediaPriority;
        degradationPreference?: DegradationPreference;
        jitterBufferTarget?: ReceiverJitterBufferTarget;
    }) => Promise<void>;
    stopPresenting: () => void;
    setStream: (stream: MediaStream) => void;
    setBandwidth: (bandwidth: number) => void;
    setDegradationPreference: (preference: DegradationPreference) => void;

    liveCaptions: (opt: {
        enable: boolean;
        conferenceAlias?: string;
        participantUuid?: ParticipantID;
        breakoutUuid?: RoomID;
        captionsLanguage?: string;
    }) => Promise<
        | undefined
        | Awaited<ReturnType<typeof showLiveCaptions | typeof hideLiveCaptions>>
    >;

    liveCaptionsLanguages: (opt: {
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof liveCaptionsLanguages>>
    >;

    setRole: (opt: {
        role: 'chair' | 'guest';
        participantUuid: ParticipantID;
        conferenceAlias?: string;
    }) => Promise<void>;

    setConferenceExtension: (conferenceExtension?: string) => void;
    setPin: (pin?: string) => void;
    setSsoToken: (ssoToken?: string) => void;
    setToken: (token: string) => void;
    setCallTag: (callTag: string) => void;

    statistics: (opt: {
        audio?: StatisticsMap['Body']['audio'];
        video?: StatisticsMap['Body']['video'];
        presentation?: StatisticsMap['Body']['presentation'];
        callUuid?: string;
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
    }) => Promise<void>;

    disconnectCall: (opt: {
        callUuid?: string;
        conferenceAlias: string;
        breakoutUuid?: string;
        breakoutParticipantUuid?: string;
        host: string;
        participantUuid: string;
        reason?: string;
    }) => Promise<void>;

    requestTheme: (opt: {
        conferenceAlias?: string;
        host?: string;
    }) => Promise<undefined | Awaited<ThemeSchema>>;

    setTextOverlay: (
        opt: OverlaytextParticipantMap['Body'] & {
            conferenceAlias?: string;
            host?: string;
            participantUuid: ParticipantID;
        },
    ) => Promise<
        undefined | Awaited<ReturnType<typeof overlaytextParticipant>>
    >;

    sendDTMF: (opt: {
        digits: string;
        participantUuid?: ParticipantID;
        callUuid?: string;
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        undefined | Awaited<ReturnType<typeof dtmfParticipant | typeof dtmf>>
    >;

    requestAspectRatio: (opt: {
        aspectRatio: number;
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        abortSignal?: AbortSignal;
    }) => Promise<undefined | Awaited<ReturnType<typeof preferredAspectRatio>>>;

    sendConferenceRequest: (opt: {
        path: string;
        method: 'GET' | 'POST';
        payload?: Record<string, unknown>;
    }) => Promise<undefined | {status: number; data: unknown}>;

    /**
     * Requests the current participant list from backend via 'participants' API call
     * @returns Response of 'participants' API call
     */
    requestParticipants: (opt: {
        conferenceAlias?: string;
        host?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof participants>>>;

    /**
     * @param participants - which participants from which rooms to move to the new breakout upon its creation
     * @returns
     */
    breakout: (
        opt: BreakoutsMap['Body'],
    ) => Promise<{breakout_uuid: RoomID} | undefined>;

    joinBreakoutRoom: (opt: {
        breakoutUuid?: RoomID;
        bandwidth?: number;
        allowVP9?: boolean;
        maxFrameSize?: number;
        mediaStream?: MediaStream | (() => MediaStream | undefined);
        callType?: ClientCallType;
        /**
         * DSCP markings for each media line
         * This has the same effect as priority, except that it only affects
         * the DSCP markings of the generated packets, as described in
         * [rfc8835] section 4.2.
         */
        networkPriorities?: MediaPriority;
        /**
         * Encoding priority for each media line
         * Indicates the priority of an RTCRtpSender, which influences the
         * bandwidth allocation among RTCRtpSender objects. It is specified in
         * [rfc8835], Section 4. The user agent is free to sub-allocate
         * bandwidth between the encodings of a RTCRtpSender.
         */
        encodingPriorities?: MediaPriority;
        degradationPreference?: DegradationPreference;
        jitterBufferTarget?: ReceiverJitterBufferTarget;
    }) => Promise<void>;
    guestLeaveBreakoutRoom: () => Promise<
        undefined | Awaited<ReturnType<typeof guestLeaveBreakout>>
    >;
    closeBreakouts: () => Promise<
        undefined | Awaited<ReturnType<typeof breakoutsDisconnect>>
    >;
    closeBreakoutRoom: (opt: {
        breakoutUuid: RoomID;
    }) => Promise<undefined | Awaited<ReturnType<typeof disconnect>>>;
    /**
     * @param fromBreakoutUuid - Optional breakout uuid to move the participants from. Defaults from main room.
     * @param toRoomUuid - Breakout room uuid to move the participants to, or `main` to move them to the main room
     * @param participants - List of participant uuids to move 'fromBreakoutUuid' to 'toRoomUuid'
     */
    emptyBreakouts: () => Promise<
        undefined | Awaited<ReturnType<typeof breakoutsEmpty>>
    >;
    breakoutMoveParticipants: (opt: {
        fromBreakoutUuid?: RoomID;
        toRoomUuid: RoomID;
        participants: ParticipantID[];
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<
                  | typeof breakoutMoveParticipants
                  | typeof breakoutMoveParticipantsFromMain
              >
          >
    >;
    startConference: (opt: {
        conferenceAlias?: string;
        host?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof startConference>>>;
    fecc: (
        opt: FeccMap['Body'] & {target?: string},
    ) => Promise<
        undefined | Awaited<ReturnType<typeof feccParticipant | typeof fecc>>
    >;
    availableLayouts: (opt: {
        conferenceAlias?: string;
        host?: string;
        annotate?: boolean;
    }) => Promise<
        | undefined
        | Awaited<ReturnType<typeof availableLayouts>>['data']['result']
    >;

    layoutSvgs: (opt: {
        conferenceAlias?: string;
        host?: string;
    }) => Promise<Record<string, string> | undefined>;
    presInMix: (opt: {
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
        state: boolean;
    }) => Promise<undefined | Awaited<ReturnType<typeof presInMixParticipant>>>;
    breakoutAskForHelp: (opt: {
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<
              ReturnType<typeof guestBreakoutBuzz | typeof hostBreakoutBuzz>
          >
    >;
    breakoutRemoveAskForHelp: (opt: {
        conferenceAlias?: string;
        breakoutUuid?: RoomID;
        host?: string;
    }) => Promise<
        | undefined
        | Awaited<
              | ReturnType<typeof guestClearBreakoutBuzz>
              | typeof hostClearBreakoutBuzz
          >
    >;
    notifyNotAFK: (opt: {
        conferenceAlias?: string;
        participantUuid?: ParticipantID;
        host?: string;
    }) => Promise<undefined | Awaited<ReturnType<typeof notifyNotAFK>>>;
}

export type InfinityClient = Omit<
    Client,
    | 'ack'
    | 'update'
    | 'newCandidate'
    | 'sendOffer'
    | 'statistics'
    | 'disconnectCall'
    | 'takeFloor'
    | 'releaseFloor'
    | 'requestTheme'
    | 'sendMessage'
> & {
    sendMessage: (opt: {
        payload: MessageBody['payload'];
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
    }) => Promise<GetEndpointResponse<'sendMessage'>>;
    sendApplicationMessage: (opt: {
        payload: Record<string, unknown>;
        conferenceAlias?: string;
        host?: string;
        participantUuid?: ParticipantID;
    }) => Promise<GetEndpointResponse<'sendMessage'>>;
    pushStatistics: () => void;
};

export type GetEndpointParams<K extends keyof Client> = Client[K] extends (
    ...args: never
) => unknown
    ? Parameters<Client[K]>
    : never;

export type GetEndpointResponse<K extends keyof Client> = Client[K] extends (
    ...args: never
) => infer R
    ? Awaited<R>
    : unknown;

export type EndpointResponse<K = keyof Client> = K extends keyof Client
    ? GetEndpointResponse<K>
    : never;

/**
 * Priority and QoS Model for each media line
 */
export interface MediaPriority {
    video: RTCPriorityType;
    audio: RTCPriorityType;
    presentation: RTCPriorityType;
}

/**
 * The jitterBufferTarget property of the RTCRtpReceiver interface
 *
 * It indicates the application's preferred duration,
 * in milliseconds, for which the jitter buffer should
 * hold media before playing it out
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/RTCRtpReceiver/jitterBufferTarget}
 */
export interface ReceiverJitterBufferTarget {
    audio?: number | null;
    video?: number | null;
    presentation?: number | null;
}
export interface DegradationPreference {
    video?: RTCDegradationPreference;
    presentation?: RTCDegradationPreference;
}

export interface AllowMediaChangeOfDirection {
    video: boolean;
    audio: boolean;
    presentation: boolean;
}

export interface CallOptions {
    timeToWaitForOfferInMS?: number;
    sendOffer: (opt: {
        sdp: string;
        breakoutUuid?: RoomID;
    }) => Promise<GetEndpointResponse<'sendOffer'>>;

    ack: Client['ack'] & {breakoutUuid?: RoomID};

    newCandidate: (opt: {
        candidate: IceCandidate;
        breakoutUuid?: RoomID;
    }) => Promise<GetEndpointResponse<'newCandidate'>>;

    update: (opt: {
        sdp: string;
        abortSignal?: AbortSignal;
        breakoutUuid?: RoomID;
    }) => Promise<GetEndpointResponse<'update'>>;

    takeFloor: Client['takeFloor'];
    releaseFloor: Client['releaseFloor'];

    getCurrentCallUuid: () => CallUuid;

    signals: InfinitySignals;
    eventSignals: EventSignals;
    callSignals: CallSignals;

    pcMainSignals: Pick<
        CorePeerConnectionSignals &
            Omit<PeerConnectionCommandSignals, 'onReceiveIceCandidate'> &
            Required<PCOptionalsSignals>,
        | 'onError'
        | 'onOfferIgnored'
        | 'onOfferRequired'
        | 'onReceiveIceCandidate'
        | 'onReceiveAnswer'
        | 'onReceiveOffer'
        | 'onOffer'
        | 'onAnswer'
        | 'onRemoteStreams'
        | 'onIceCandidate'
        | 'onSignalingStateChange'
        | 'onConnectionStateChange'
        | 'onIceConnectionStateChange'
        | 'onTransceiverChange'
        | 'onSecureCheckCode'
    >;
    mainStatsSignals: MainStatsSignals;

    peerOptions: MainPeerConnectionOptions;
    dataChannelId?: number;

    mediaStream?: MediaStream;
    isDirectMedia: boolean;
    callType?: ClientCallType;
    allowChangeOfMediaDirection?: AllowMediaChangeOfDirection;
    /**
     * DSCP markings for each media line
     * This has the same effect as priority, except that it only affects
     * the DSCP markings of the generated packets, as described in
     * [rfc8835] section 4.2.
     */
    networkPriorities?: MediaPriority;
    /**
     * Encoding priority for each media line
     * Indicates the priority of an RTCRtpSender, which influences the
     * bandwidth allocation among RTCRtpSender objects. It is specified in
     * [rfc8835], Section 4. The user agent is free to sub-allocate
     * bandwidth between the encodings of an RTCRtpSender.
     */
    encodingPriorities?: MediaPriority;
    /**
     * DegradationPreference for video and preso
     */
    degradationPreference?: DegradationPreference;
    jitterBufferTarget?: ReceiverJitterBufferTarget;
}

export interface Call {
    presoState: PresoState;
    disconnect: () => void;
    present: (stream?: MediaStream) => Promise<void>;
    receivePresentation: (event: NormalizedPresentationEvent) => Promise<void>;
    stopPresenting: () => Promise<void>;
    stopReceivingPresentation: () => void;
    setStream: (stream: MediaStream) => void;
    setBandwidth: (bandwidth: Bandwidth) => void;
    setDegradationPreference: (preference: DegradationPreference) => void;
    sendDataChannelEvent: (msg: DataChannelEvent) => void;
    mediaStream?: MediaStream;
    bandwidth: Bandwidth;
    allowVP9?: number;
    maxFrameSize?: number;
}

export interface PresoConnectionChangeEvent {
    send: RTCPeerConnectionState;
    recv: RTCPeerConnectionState;
}

export type ExtendedInfinityErrorCode = InfinityErrorCode | '#pex128';

export type ErrorSignal = Signal<{
    error: ExtendedInfinityErrorMessage;
    errorCode: ExtendedInfinityErrorCode;
}>;

export interface ClientSignals {
    /**
     * Signal when the event source is connected
     */
    onConnected: Signal<undefined>;
    onDisconnected: ErrorSignal;
    /**
     * Signal when the Peer Connection is connected
     */
    onCallConnected: Signal<undefined>;
    onCallDisconnected: Signal<CallDisconnectedEvent>;
    onAnswer: Signal<CallsWebrtcParticipantMap['200']['result']>;
    onPresentationAnswer: Signal<{
        sdp: string;
        callUuid: string;
        present: string;
    }>;
    onIceCandidate: Signal<RTCIceCandidate>;
    onFailedRequest: Signal<keyof Client>;
    onRetryQueueFlushed: Signal<undefined>;
}

interface RoomEvent {
    id: RoomID;
}

interface RoomParticipantsEvent extends RoomEvent {
    participants: Participant[];
}
export interface RoomParticipantEvent extends RoomEvent {
    participant: Participant;
}
interface RoomConferenceEvent extends RoomEvent {
    status: ConferenceStatus;
}

export interface ParticipantSignals {
    onParticipants: Signal<RoomParticipantsEvent>;
    onParticipantUpdated: Signal<RoomParticipantEvent, RoomParticipantEvent[]>;
    onParticipantJoined: Signal<RoomParticipantEvent, RoomParticipantEvent[]>;
    onParticipantLeft: Signal<RoomParticipantEvent, RoomParticipantEvent[]>;
    onMe: Signal<RoomParticipantEvent>;
    onRaiseHand: Signal<RoomParticipantEvent>;
    /**
     * Cancel all batched signals
     */
    cancel(): void;
}

export interface InfinitySignals extends ClientSignals, ParticipantSignals {
    onAuthenticatedWithConference: Signal<{
        conferenceAlias: string;
        conferenceName?: string;
    }>;
    onTokenRefreshed: Signal<{token: string}>;
    onTokenExpired: Signal<string>;
    onError: ErrorSignal;
    onPinRequired: Signal<{hasHostPin: boolean; hasGuestPin: boolean}>;
    onIdp: Signal<Idp[]>;
    onDisplayNameRequired: Signal<undefined>;
    onRedirect: Signal<{
        redirectUrl: string;
        redirectIdp: RedirectIdp;
        disablePopupFlow?: boolean;
    }>;
    onMessage: Signal<Message>;
    onApplicationMessage: Signal<ApplicationMessage>;
    onMyselfMuted: Signal<{
        isServerMuted: boolean;
        hasServerMuteStateChanged: boolean;
    }>;
    onRequestedLayout: Signal<{
        primaryScreen: {hostLayout: Layout; guestLayout: Layout};
    }>;
    onLayoutOverlayTextEnabled: Signal<boolean>;
    onStage: Signal<Stage[]>;
    onConferenceStatus: Signal<RoomConferenceEvent>;
    onTransfer: Signal<TransferDetails>;
    onCancelTransfer: Signal<Record<string, string>>;
    onLiveCaptions: Signal<{
        data: string;
        isFinal: boolean;
        speakers: LiveCaptionSpeaker[];
    }>;
    onSplashScreen: Signal<SplashScreen>;
    onNewOffer: Signal<string>;
    onUpdateSdp: Signal<string>;
    onPeerDisconnect: Signal<undefined>;
    onExtension: Signal<string>;
    onLayoutUpdate: EventSignals['onLayoutUpdate'];
    onServiceType: Signal<CurrentServiceType>;
    onBreakoutEnd: Signal<BreakoutRoom>;
    onBreakoutBegin: Signal<BreakoutRoom>;
    onBreakoutRefer: Signal<BreakoutReferDetails>;
    onFecc: Signal<FeccEvent>;
}

export type InfinitySignalsOptional = Pick<
    Partial<InfinitySignals>,
    'onPinRequired' | 'onParticipants' | 'onError' | 'onCallConnected'
>;
export type InfinitySignalsRequired = Pick<
    InfinitySignals,
    'onConnected' | 'onAnswer' | 'onPresentationAnswer' | 'onDisconnected'
>;

export interface MainStatsSignals {
    audioIn: StatsSignals;
    audioOut: StatsSignals;
    videoIn: StatsSignals;
    videoOut: StatsSignals;
    presoVideoIn: StatsSignals;
    presoVideoOut: StatsSignals;
    combinedRtcStatsSignal: Signal<
        [
            NormalizedRTCStats | undefined,
            NormalizedRTCStats | undefined,
            NormalizedRTCStats | undefined,
            NormalizedRTCStats | undefined,
            NormalizedRTCStats | undefined,
            NormalizedRTCStats | undefined,
        ]
    >;
    combinedCallQualitySignal: Signal<
        [Quality | undefined, Quality | undefined]
    >;
    combinedCallQualityStatsSignal: Signal<
        [CallQualityStats | undefined, CallQualityStats | undefined]
    >;
}

export interface Stats {
    inbound: {
        audio?: InboundAudioMetrics;
        video?: InboundVideoMetrics;
        preso?: InboundVideoMetrics;
    };
    outbound: {
        audio?: OutboundAudioMetrics;
        video?: OutboundVideoMetrics;
        preso?: OutboundVideoMetrics;
    };
}

export interface Incoming {
    conference_alias: string;
    local_alias: string;
    remote_alias: string;
    remote_display_name: string;
    service_type: string;
    call_type: string;
    callback_available: boolean;
    token: string;
}

export interface IncomingCancelled {
    token: string;
}

export interface RegistrationSignals {
    onIncoming: Signal<Incoming>;
    onIncomingCancelled: Signal<IncomingCancelled>;
}
export interface CallSignals {
    onRemoteStream: Signal<MediaStream>;
    onRemotePresentationStream: Signal<MediaStream>;
    onCallConnected: Signal<undefined>;
    onPresentationConnectionChange: Signal<PresoConnectionChangeEvent>;
    onRtcStats: Signal<Stats>;
    onCallQualityStats: Signal<{
        inbound: {audio: AudioQualityStats};
        outbound: {audio: AudioQualityStats};
    }>;
    onCallQuality: Signal<[Quality, Quality]>;
    onSecureCheckCode: Signal<string>;
    onReconnecting: Signal<undefined>;
    onReconnected: Signal<undefined>;
}

export type InfinityCallSignalsOptional = Pick<
    Partial<CallSignals>,
    'onCallConnected'
>;
export type InfinityCallRequired = Pick<
    CallSignals,
    | 'onRemoteStream'
    | 'onRemotePresentationStream'
    | 'onPresentationConnectionChange'
    | 'onRtcStats'
    | 'onCallQualityStats'
    | 'onCallQuality'
>;
export interface SplashScreen {
    text: string;
    background: string;
    screenKey: keyof ThemeSchema | 'custom';
    displayDuration: number;
}
export interface EventSignals {
    onConnected: Signal<undefined>;
    onError: Signal<undefined>;
    onPresentationStart: Signal<NormalizedPresentationEvent>;
    onPresentationStop: Signal<undefined>;
    onPresentationEnded: Signal<undefined>;
    onPresentationFrame: Signal<string>;
    onParticipantCreate: Signal<Participant>;
    onParticipantUpdate: Signal<Participant>;
    onParticipantDelete: Signal<string>;
    onMessage: Signal<MessageEvent>;
    onConferenceUpdate: Signal<ConferenceStatus>;
    onStageUpdate: Signal<StageEvent[]>;
    onLayoutUpdate: Signal<LayoutEvent>;
    onCallDisconnected: Signal<CallDisconnectedEvent>;
    onDisconnect: Signal<DisconnectEvent>;
    onParticipantSyncBegin: Signal<undefined>;
    onParticipantSyncEnd: Signal<undefined>;
    onRefer: Signal<{
        alias: string;
        token: string;
        breakout_name?: string;
        target?: 'main' | 'breakout' | 'conference';
    }>;
    onCancelRefer: Signal<Record<string, string>>;
    onHold: Signal<boolean>;
    onFecc: Signal<FeccEvent>;
    onRefreshToken: Signal<undefined>;
    onLiveCaptions: Signal<LiveCaptionsEvent>;
    onPeerDisconnect: Signal<undefined>;
    onNewOffer: Signal<{sdp: string}>;
    onUpdateSdp: Signal<{sdp: string}>;
    onNewCandidate: Signal<IceCandidate>;
    onSplashScreen: Signal<{
        screen_key: keyof ThemeSchema;
        display_duration: number;
    } | null>;
    onBreakoutBegin: Signal<BreakoutRoom>;
    // TODO: This basically syncs all the events from breakout rooms so essentially you can get any og the above in here
    // Find a better way to share the code
    onBreakoutEvent: Signal<
        {
            breakout_uuid: string;
        } & (
            | {event: 'participant_create'; data: RTCParticipantEvent}
            | {event: 'participant_update'; data: RTCParticipantEvent}
            | {event: 'participant_delete'; data: RTCParticipantEvent}
            | {event: 'participant_sync_begin'}
            | {event: 'participant_sync_end'}
            | {event: 'message_received'; data: MessageEvent}
            | {event: 'conference_update'; data: ConferenceStateEvent}
            | {event: 'presentation_stop'}
            | {event: 'presentation_start'; data: PresentationEvent}
            | {event: 'live_captions'; data: LiveCaptionsEvent}
        )
    >;
    onBreakoutEnd: Signal<BreakoutRoom>;
    onBreakoutRefer: Signal<{
        breakout_uuid: RoomID;
        breakout_name: string;
        requester_uuid?: ParticipantID;
    }>;
}

export type InfinityEventSignalsOptional = Pick<
    Partial<EventSignals>,
    'onHold' | 'onRefer' | 'onFecc' | 'onRefreshToken'
>;

export type SignalName =
    keyof CallSignals | keyof EventSignals | keyof InfinitySignals;

export interface IceCandidate {
    candidate: string;
    mid: string;
    ufrag?: string;
}

export type ClientSideErrorMessage =
    | PCErrorMessage
    | 'Could not reconnect to the meeting'
    | 'Could not execute critical network action'
    | 'Could not find ICE candidates'
    | 'WebRTC connection closed'
    | 'WebRTC connection failed';

export type ExtendedInfinityErrorMessage =
    InfinityErrorMessage | ClientSideErrorMessage;

export interface DisconnectEvent {
    reason: InfinityErrorMessage;
}

export interface MessageEvent extends MessageBody {
    origin: string;
    uuid: ParticipantID;
    direct: boolean;
}

export interface StageEvent {
    participant_uuid: string;
    stage_index: number;
    vad: number;
}

export interface LayoutEvent {
    requested_layout: {
        primary_screen: {
            chair_layout: Layout;
            guest_layout: Layout;
        };
    };
    view: Layout;
    participants: ParticipantID[];
    overlay_text_enabled?: boolean;
    /**
     * Coordinates of the presentation slot in the layout, if presentation is in mix.
     * 4 pairs of floats where each pair is a corner of the presentation slot
     */
    pres_slot_coords?: [
        [number, number],
        [number, number],
        [number, number],
        [number, number],
    ];
}

export interface PresentationEvent {
    status?: 'start' | 'stop' | 'newframe';
    presenter_name?: string;
    presenter_uri?: string;
    presenter_uuid?: ParticipantID;
    lastEventId?: string;
}

export interface NormalizedPresentationEvent {
    presenterDisplayName: string;
    presenterName?: string;
    presenterUri?: string;
    presenterUuid?: ParticipantID;
}

export type Sources = Source[];
export interface Source {
    participant_uuid: string;
    indices: number[];
}
/**
 * A participant credited as a speaker for a live captions fragment.
 */
export interface LiveCaptionSpeaker {
    participantUuid: ParticipantID;
    displayName: string;
}
export interface LiveCaptionsEvent {
    data: string;
    is_final: boolean;
    src_lang?: string;
    tgt_lang?: string;
    sources?: Sources;
}

export interface CallDisconnectedEvent {
    call_uuid: string;
    reason?: DisconnectEvent;
}

export type EventsSourceType =
    | DisconnectEvent
    | MessageEvent
    | ConferenceStateEvent
    | PresentationEvent
    | RTCParticipantEvent
    | FeccEvent;

export enum CallType {
    audio = 'audio',
    video = 'video',
    api = 'api',
}
export interface Participant {
    callType: CallType;
    canControl: boolean;
    canChangeLayout: boolean;
    canDisconnect: boolean;
    canMute: boolean;
    canTransfer: boolean;
    canFecc: boolean;
    canRaiseHand: boolean;
    canSpotlight: boolean;
    displayName?: string;
    overlayText: string;
    handRaisedTime: number;
    isCameraMuted: boolean;
    isConjoined: boolean;
    isConnecting: boolean;
    isEndpoint?: boolean;
    isExternal: boolean;
    isIdpAuthenticated?: boolean;
    isGateway: boolean;
    isHost: boolean;
    isMainVideoDroppedOut: boolean;
    isClientMuted: boolean;
    isMuted: boolean;
    isPresenting: boolean;
    isSpotlight: boolean;
    isStreaming: boolean;
    isTransferring: boolean;
    isVideoSilent: boolean;
    isWaiting: boolean;
    needsPresentationInMix: boolean;
    protocol: RTCParticipantEvent['protocol'];
    hasMedia: boolean;
    raisedHand: boolean;
    role: RTCParticipantEvent['role'];
    rxPresentation: boolean;
    serviceType: RTCParticipantEvent['service_type'];
    spotlightOrder: number;
    supportsDirectChat: boolean;
    startAt: Date | null;
    startTime: number | null;
    uri: string;
    uuid: ParticipantID;
    vendor: RTCParticipantEvent['vendor'];
    canReceivePersonalMix: boolean;
    receiveFromVideoMix: RTCParticipantEvent['receive_from_video_mix'];
    rawData: RTCParticipantEvent;
}

export interface Message {
    at: Date;
    id: string;
    message: string;
    displayName: string;
    userId: ParticipantID;
    direct: boolean;
}

export interface ApplicationMessage extends Omit<Message, 'message'> {
    message: Record<string, unknown>;
}

export interface Stage {
    userId: string;
    stageIndex: number;
    vad: number;
}

export interface ConferenceStatus {
    locked: boolean;
    guestsMuted: boolean;
    allMuted: boolean;
    started: boolean;
    liveCaptionsAvailable: boolean;
    liveCaptionsAvailableLanguages: string[];
    liveCaptionsSourceLanguages: string[];

    breakoutRooms: boolean;
    directMedia: boolean;
    presentationAllowed: boolean;
    classification?: {
        current: number;
        levels: Record<number, string>;
    };
    breakout: boolean;
    breakoutName?: string;
    breakoutDescription?: string;
    endTime?: number;
    endAction?: 'transfer' | 'disconnect';
    guestsAllowedToLeave?: boolean;
    breakoutbuzz?: {
        time: number;
        value: boolean;
    };
    guestsCanUnmute?: boolean;
    guestsCanPresent?: boolean;
    chatEnabled?: boolean;
    rawData: ConferenceStateEvent;
}

export interface TransferDetails {
    alias: string;
    token: string;
    callTag?: string;
    breakoutName?: string;
    target?: 'main' | 'breakout' | 'conference' | 'direct' | 'transcoded';
}

export interface BreakoutReferDetails {
    breakoutUuid: RoomID;
    breakoutName: string;
    requesterUuid?: ParticipantID;
}

export interface ConferenceFeatureFlags {
    chatEnabled: boolean;
    guestsCanPresent: boolean;
    isDirectMedia: boolean;
    breakoutRoomsEnabled: boolean;
    callType: 'video' | 'audio' | 'video-only';
}

export type CallUuid = string | undefined;

export interface PresoState {
    send: RTCPeerConnectionState;
    recv: RTCPeerConnectionState;
}

export interface StatsCollectors {
    inbound: {
        audio?: StatsCollector;
        video?: StatsCollector;
        preso?: StatsCollector;
    };
    outbound: {
        audio?: StatsCollector;
        video?: StatsCollector;
        preso?: StatsCollector;
    };
}

export type DataChannelEvent =
    | {
          type: 'message';
          body: Omit<MessageEvent, 'direct'>;
      }
    | {
          type: 'fecc';
          body: FeccEvent;
      };

export type DisconnectReason =
    | ExtendedInfinityErrorMessage
    | 'Browser closed'
    | 'User initiated disconnect'
    | 'Transfer'
    | 'DirectMediaTransfer';

export type SSOdMessage =
    | {result: 'forbidden'; code: 403}
    | {result: 'success'; token: string}
    | {result: 'service unavailable'; code: 503};

export type Fetch = typeof window.fetch;
export type GenerateEndpointParams<T extends keyof Client> =
    | {
          func: (
              reqParams: GetEndpointParams<T>,
          ) => Promise<GetEndpointResponse<T>>;
          funcName: keyof Client;
          retriable?: boolean;
          fetcher: Fetch;
          requiresToken: false;
      }
    | {
          func: (
              reqParams: GetEndpointParams<T>,
              fetcher: Fetch,
          ) => Promise<GetEndpointResponse<T>>;
          funcName: T;
          retriable?: boolean;
          fetcher: Fetch;
          requiresToken: true;
      };

export type GenerateEndpoint = <T extends keyof Client>(
    opts: GenerateEndpointParams<T>,
) => (
    ...args: GetEndpointParams<T>
) => Promise<GetEndpointResponse<T> | undefined>;

// export interface Client {
//     generateEndpoint: GenerateEndpoint;
// }

export type Logger = {
    log: LogMethod;
    redact: (text: string) => string;
};
export type CreateFetcher = (logger: Logger) => Fetch;
export interface ClientProps {
    host: string;
    conferenceAlias: string;
    participantUuid: ParticipantID;
    breakoutUuid?: RoomID;
    chatEnabled: boolean;
    guestsCanPresent: boolean;
    isDirectMedia: boolean;
    breakoutRoomsEnabled: boolean;
    serviceType: CurrentServiceType;
}
